import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Ticket,
  ShoppingCart,
  AlertCircle,
  CheckCircle,
  Download,
  CreditCard,
  Shield,
  Info,
  Clock,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  ParticipantFormData,
  Ticket as TicketType,
  TicketTypeOption,
  TicketInventory,
} from "../types";
import { TICKET_PRICES, SERVICE_FEE } from "../config/stripe";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";

interface ParticipantFormProps {
  index: number;
  data: ParticipantFormData;
  onChange: (index: number, data: ParticipantFormData) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  t: ReturnType<typeof useLanguage>["t"];
  inventory: TicketInventory;
}

/** Check if a participant is 10 years old or younger based on birth date */
function isChildEligible(dataNascita: string): boolean {
  if (!dataNascita) return false;
  const birthDate = new Date(dataNascita);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age < 10;
}

const ParticipantForm: React.FC<ParticipantFormProps> = ({
  index,
  data,
  onChange,
  onRemove,
  canRemove,
  t,
  inventory,
}) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const newData = {
      ...data,
      [e.target.name]: e.target.value,
    };

    onChange(index, newData);
  };

  const handleTicketTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as TicketTypeOption;
    onChange(index, { ...data, ticketType: value });
  };

  const ticketPrice = TICKET_PRICES[data.ticketType];

  return (
    <div className="card p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg text-gray-900">
          {t.tickets.participant} {index + 1}
        </h3>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Ticket Type Selector */}
      <div className="mb-4">
        <label className="label">{t.ticketTypes.selectType} *</label>
        <select
          name="ticketType"
          value={data.ticketType}
          onChange={handleTicketTypeChange}
          className="input"
        >
          <option value="vip" disabled={inventory.vip <= 0}>
            {t.ticketTypes.vip} - €{TICKET_PRICES.vip.toFixed(2)}
            {inventory.vip <= 0 ? ` (${t.ticketTypes.soldOut})` : ""}
          </option>
          <option value="normal" disabled={inventory.normal <= 0}>
            {t.ticketTypes.normal} - €{TICKET_PRICES.normal.toFixed(2)}
            {inventory.normal <= 0 ? ` (${t.ticketTypes.soldOut})` : ""}
          </option>
          <option value="child">
            {t.ticketTypes.child} - €{TICKET_PRICES.child.toFixed(2)}
          </option>
        </select>
        <p className="text-xs text-gray-500 mt-1 whitespace-pre-line">
          {data.ticketType === "vip" && t.ticketTypes.vipDesc}
          {data.ticketType === "normal" && t.ticketTypes.normalDesc}
          {data.ticketType === "child" && t.ticketTypes.childDesc}
        </p>
        <p className="text-sm font-medium text-romanian-blue mt-1">
          €{ticketPrice.toFixed(2)}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">{t.participant.firstName} *</label>
          <input
            type="text"
            name="nome"
            value={data.nome}
            onChange={handleChange}
            required
            className="input"
            placeholder={t.participant.firstName}
          />
        </div>
        <div>
          <label className="label">{t.participant.lastName} *</label>
          <input
            type="text"
            name="cognome"
            value={data.cognome}
            onChange={handleChange}
            required
            className="input"
            placeholder={t.participant.lastName}
          />
        </div>
        <div>
          <label className="label">{t.participant.birthDate} *</label>
          <input
            type="date"
            name="dataNascita"
            value={data.dataNascita}
            onChange={handleChange}
            required
            className="input"
          />
        </div>

        <div>
          <label className="label">{t.participant.phone} *</label>
          <input
            type="tel"
            name="cellulare"
            value={data.cellulare}
            onChange={handleChange}
            required
            className="input"
            placeholder="+39 393 581 7177"
          />
        </div>
        <div>
          <label className="label">{t.participant.fiscalCode} *</label>
          <input
            type="text"
            name="codiceFiscale"
            value={data.codiceFiscale}
            onChange={handleChange}
            required
            maxLength={16}
            className="input uppercase"
            placeholder="RSSMRA85M01H501Z"
          />
        </div>
        <div>
          <label className="label">{t.participant.email} *</label>
          <input
            type="email"
            name="email"
            value={data.email}
            onChange={handleChange}
            required
            className="input"
            placeholder="email@esempio.com"
          />
        </div>
      </div>
    </div>
  );
};

const emptyParticipant: ParticipantFormData = {
  nome: "",
  cognome: "",
  dataNascita: "",
  luogoNascita: "",
  cellulare: "",
  codiceFiscale: "",
  email: "",
  citta: "",
  via: "",
  numeroCivico: "",
  ticketType: "normal",
};

const EVENT_DATE = new Date("2026-04-06T15:00:00+02:00");

const Tickets: React.FC = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [participants, setParticipants] = useState<ParticipantFormData[]>([
    { ...emptyParticipant },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userTickets, setUserTickets] = useState<TicketType[]>([]);
  const [, setLoadingTickets] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [inventory, setInventory] = useState<TicketInventory>({
    vip: 0,
    normal: 0,
  });
  const [loadingInventory, setLoadingInventory] = useState(true);

  // Fake "people viewing" counter — after 22:00 stays 1-3, otherwise 1-14
  const [viewerCount, setViewerCount] = useState<number>(() => {
    const hour = new Date().getHours();
    return hour >= 22
      ? 1 + Math.floor(Math.random() * 3)
      : 1 + Math.floor(Math.random() * 14);
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      // Random delay 3-8 seconds for unpredictable feel
      const delay = 3000 + Math.random() * 5000;
      timeoutId = setTimeout(() => {
        setViewerCount((prev) => {
          const hour = new Date().getHours();
          const maxViewers = hour >= 22 ? 3 : 14;
          const rand = Math.random();
          let delta: number;
          if (rand < 0.55)
            delta = -1; // 55%: -1 (urgency: seats filling)
          else if (rand < 0.85)
            delta = 1; // 30%: +1 (others joining)
          else delta = 0; // 15%: no change
          const next = prev + delta;
          return Math.min(maxViewers, Math.max(1, next));
        });
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, []);

  // Countdown timer to event date
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, EVENT_DATE.getTime() - now);
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch ticket availability
  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    setLoadingInventory(true);
    try {
      const getTicketAvailability = httpsCallable(
        functions,
        "getTicketAvailability",
      );
      const result = await getTicketAvailability();
      const data = result.data as TicketInventory;
      setInventory(data);
    } catch (err) {
      console.error("Error fetching ticket availability:", err);
    } finally {
      setLoadingInventory(false);
    }
  };

  // Fetch user's existing tickets
  useEffect(() => {
    if (currentUser) {
      fetchUserTickets();
    }
  }, [currentUser]);

  const fetchUserTickets = async () => {
    if (!currentUser) return;

    setLoadingTickets(true);
    try {
      const getUserTickets = httpsCallable(functions, "getUserTickets");
      const result = await getUserTickets({ uid: currentUser.uid });
      const data = result.data as { tickets: TicketType[] };
      setUserTickets(data.tickets || []);
    } catch (err) {
      console.error("Error fetching tickets:", err);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleParticipantChange = (
    index: number,
    data: ParticipantFormData,
  ) => {
    const newParticipants = [...participants];
    newParticipants[index] = data;
    setParticipants(newParticipants);
  };

  const addParticipant = () => {
    if (participants.length < 10) {
      setParticipants([...participants, { ...emptyParticipant }]);
    }
  };

  const removeParticipant = (index: number) => {
    if (participants.length > 1) {
      setParticipants(participants.filter((_, i) => i !== index));
    }
  };

  const validateParticipants = (): boolean => {
    for (const p of participants) {
      if (
        !p.nome ||
        !p.cognome ||
        !p.dataNascita ||
        !p.cellulare ||
        !p.codiceFiscale ||
        !p.email
      ) {
        setError(t.participant.required);
        return false;
      }
      if (p.codiceFiscale.length !== 16) {
        setError(t.participant.fiscalCodeLength);
        return false;
      }
      // Validate child ticket age
      if (p.ticketType === "child" && !isChildEligible(p.dataNascita)) {
        setError(t.ticketTypes.childAgeError);
        return false;
      }
      // Validate inventory availability
      if (p.ticketType === "vip" && inventory.vip <= 0) {
        setError(t.ticketTypes.soldOut + " (" + t.ticketTypes.vip + ")");
        return false;
      }
      if (p.ticketType === "normal" && inventory.normal <= 0) {
        setError(t.ticketTypes.soldOut + " (" + t.ticketTypes.normal + ")");
        return false;
      }
    }

    // Count each type and validate against inventory
    const vipCount = participants.filter((p) => p.ticketType === "vip").length;
    const normalCount = participants.filter(
      (p) => p.ticketType === "normal",
    ).length;
    if (vipCount > inventory.vip) {
      setError(t.ticketTypes.soldOut + " (" + t.ticketTypes.vip + ")");
      return false;
    }
    if (normalCount > inventory.normal) {
      setError(t.ticketTypes.soldOut + " (" + t.ticketTypes.normal + ")");
      return false;
    }

    return true;
  };

  const handleCheckout = async () => {
    if (!currentUser) {
      navigate("/login", { state: { from: { pathname: "/acces" } } });
      return;
    }

    if (!validateParticipants()) {
      return;
    }

    if (!acceptedTerms) {
      setError(t.tickets.acceptTermsError);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const createCheckoutSession = httpsCallable(
        functions,
        "createCheckoutSession",
      );
      const result = await createCheckoutSession({
        userId: currentUser.uid,
        userEmail: currentUser.email,
        participants,
        origin: window.location.origin,
      });

      const data = result.data as {
        url: string | null;
        free: boolean;
        orderId: string;
      };

      if (data.free) {
        // Free ticket (all children) - redirect to success page
        navigate(`/payment-success?order_id=${data.orderId}`);
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError(t.common.checkoutError);
    } finally {
      setLoading(false);
    }
  };

  const totalPrice = participants.reduce((sum, p) => {
    const ticketPrice = TICKET_PRICES[p.ticketType];
    const fee = p.ticketType === "child" ? 0 : SERVICE_FEE;
    return sum + ticketPrice + fee;
  }, 0);

  // Show login prompt if not authenticated
  if (!authLoading && !currentUser) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card p-12">
              <div className="w-20 h-20 bg-romanian-yellow/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Ticket className="w-10 h-10 text-romanian-blue" />
              </div>
              <h1 className="font-display text-3xl font-bold text-romanian-blue mb-4">
                {t.tickets.title}
              </h1>
              <p className="text-gray-600 mb-8">{t.tickets.loginRequired}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() =>
                    navigate("/login", {
                      state: { from: { pathname: "/acces" } },
                    })
                  }
                  className="btn-primary"
                >
                  {t.tickets.authentication}
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="btn-outline"
                >
                  {t.tickets.registration}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-romanian-blue to-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-romanian-yellow rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-romanian-red rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Ticket className="w-16 h-16 text-romanian-yellow mx-auto mb-4" />
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
              {t.tickets.title}
            </h1>
            <p className="text-white/90 text-lg">{t.tickets.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="section bg-gray-50">
        <div className="container-custom">
          {/* User's Existing Tickets */}
          {userTickets.length > 0 && (
            <div className="mb-12">
              <h2 className="font-display text-2xl font-bold text-romanian-blue mb-6">
                {t.tickets.yourTickets}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {userTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="card p-6 border-l-4 border-romanian-yellow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {ticket.participant?.nome}{" "}
                          {ticket.participant?.cognome}
                        </p>
                        <p className="text-sm text-gray-500">
                          {ticket.ticketCode}
                        </p>
                      </div>
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                    <a
                      href={ticket.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline w-full flex items-center justify-center space-x-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>{t.tickets.downloadPdf}</span>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Purchase Form */}
            <div className="lg:col-span-2">
              {/* Ticket Types Description */}
              <div className="card p-5 mb-6 bg-gradient-to-r from-romanian-blue/5 to-romanian-yellow/5 border border-romanian-blue/20">
                <div className="flex items-start space-x-3 mb-3">
                  <Info className="w-5 h-5 text-romanian-blue flex-shrink-0 mt-0.5" />
                  <h3 className="font-semibold text-lg text-romanian-blue">
                    {t.ticketTypes.descriptionTitle}
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-700 ml-8">
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-romanian-yellow rounded-full flex-shrink-0 mt-1.5" />
                    <span className="whitespace-pre-line">
                      {t.ticketTypes.descriptionVip}
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-romanian-blue rounded-full flex-shrink-0 mt-1.5" />
                    <span className="whitespace-pre-line">
                      {t.ticketTypes.descriptionNormal}
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0 mt-1.5" />
                    <span className="whitespace-pre-line">
                      {t.ticketTypes.descriptionChild}
                    </span>
                  </li>
                </ul>
                <div className="mt-4 pt-3 border-t border-romanian-blue/20">
                  <p className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-romanian-blue flex-shrink-0" />
                    <span>{t.ticketTypes.nonRefundable}</span>
                  </p>
                </div>
              </div>

              {/* Remaining Tickets Alert */}
              {!loadingInventory && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {inventory.vip > 0 ? (
                    <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-center">
                      <span className="text-yellow-800 font-semibold text-sm">
                        🔥{" "}
                        {t.ticketTypes.remainingVip.replace(
                          "{count}",
                          String(inventory.vip),
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className="p-3 bg-red-50 border border-red-300 rounded-lg text-center">
                      <span className="text-red-700 font-semibold text-sm">
                        {t.ticketTypes.vip} - {t.ticketTypes.soldOut}
                      </span>
                    </div>
                  )}
                  {inventory.normal > 0 ? (
                    <div className="p-3 bg-blue-50 border border-blue-300 rounded-lg text-center">
                      <span className="text-blue-800 font-semibold text-sm">
                        🔥{" "}
                        {t.ticketTypes.remainingNormal.replace(
                          "{count}",
                          String(inventory.normal),
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className="p-3 bg-red-50 border border-red-300 rounded-lg text-center">
                      <span className="text-red-700 font-semibold text-sm">
                        {t.ticketTypes.normal} - {t.ticketTypes.soldOut}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              {participants.map((participant, index) => (
                <ParticipantForm
                  key={index}
                  index={index}
                  data={participant}
                  onChange={handleParticipantChange}
                  onRemove={removeParticipant}
                  canRemove={participants.length > 1}
                  t={t}
                  inventory={inventory}
                />
              ))}

              <div className="flex justify-center mt-6">
                <button
                  onClick={addParticipant}
                  disabled={participants.length >= 10}
                  className="btn-outline flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t.tickets.addParticipant}</span>
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="card p-6 sticky top-24">
                <h3 className="font-semibold text-lg text-gray-900 mb-4 flex items-center space-x-2">
                  <ShoppingCart className="w-5 h-5" />
                  <span>{t.tickets.orderSummary}</span>
                </h3>

                <div className="space-y-3 mb-6">
                  {participants.map((p, i) => {
                    const typeLabel =
                      p.ticketType === "vip"
                        ? t.ticketTypes.vipCheckout
                        : p.ticketType === "child"
                          ? t.ticketTypes.childCheckout
                          : t.ticketTypes.normalCheckout;
                    const ticketPrice = TICKET_PRICES[p.ticketType];
                    const serviceFee =
                      p.ticketType === "child" ? 0 : SERVICE_FEE;
                    return (
                      <div key={i} className="space-y-1">
                        <div className="flex justify-between text-gray-600 text-sm">
                          <span>
                            {t.tickets.participant} {i + 1} ({typeLabel})
                          </span>
                          <span>€{ticketPrice.toFixed(2)}</span>
                        </div>
                        {serviceFee > 0 && (
                          <div className="flex justify-between text-gray-500 text-xs ml-4">
                            <span>{t.tickets.serviceFee}</span>
                            <span>€{serviceFee.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="border-t pt-3">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>{t.tickets.total}</span>
                      <span className="text-romanian-blue">
                        €{totalPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="mb-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 text-romanian-blue border-gray-300 rounded focus:ring-romanian-blue"
                    />
                    <span className="text-sm text-gray-600">
                      {t.tickets.termsAccept}{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-romanian-blue hover:underline"
                      >
                        {t.tickets.termsLink}
                      </a>{" "}
                      {t.tickets.termsAnd}{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-romanian-blue hover:underline"
                      >
                        {t.tickets.privacyLink}
                      </a>{" "}
                      *
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={loading || !acceptedTerms}
                  className="btn-primary w-full flex items-center justify-center space-x-2 mb-4"
                >
                  {loading ? (
                    <>
                      <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                      <span>{t.tickets.processing}</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      <span>{t.tickets.continuePayment}</span>
                    </>
                  )}
                </button>

                {/* Trust Badges */}
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-green-500" />
                    <span>{t.tickets.securePayment}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Download className="w-4 h-4 text-romanian-blue" />
                    <span>{t.tickets.instantPdf}</span>
                  </div>
                </div>

                {/* Countdown to event */}
                <div className="mt-5 rounded-lg border border-romanian-blue/20 bg-romanian-blue/5 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Clock className="w-4 h-4 text-romanian-blue flex-shrink-0" />
                    <span className="text-xs font-semibold text-romanian-blue uppercase tracking-wide">
                      {t.tickets.countdownLabel}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 text-center">
                    {[
                      { value: countdown.days, label: t.tickets.days },
                      { value: countdown.hours, label: t.tickets.hours },
                      { value: countdown.minutes, label: t.tickets.minutes },
                      { value: countdown.seconds, label: t.tickets.seconds },
                    ].map(({ value, label }) => (
                      <div
                        key={label}
                        className="rounded-md bg-white border border-romanian-blue/15 py-2"
                      >
                        <p className="text-xl font-extrabold text-romanian-blue tabular-nums leading-none">
                          {String(value).padStart(2, "0")}
                        </p>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          {label}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-romanian-blue/80 mt-2 text-center font-medium">
                    {t.tickets.eventApproaching}
                  </p>
                </div>

                {/* Live Viewers Counter */}
                <div className="mt-5 rounded-lg bg-gray-50 border border-gray-200 p-3">
                  <div className="flex items-center space-x-2">
                    {/* Pulsing live dot */}
                    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-600 opacity-60" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 text-red-600" />
                    </span>
                    <p className="text-sm text-gray-600">
                      <span className="font-bold text-gray-800 tabular-nums">
                        {viewerCount}
                      </span>{" "}
                      {t.tickets.viewersNow}
                    </p>
                  </div>
                  <p className="text-xs text-red-600 mt-1 ml-4.5">
                    {t.tickets.viewersUrgency}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tickets;
